import { Box, Stack, Typography } from '@mui/material';
import { type Search, SearchType } from '@tyro/api';
import { Avatar } from '@tyro/core';
import { useTranslation } from '@tyro/i18n';
import type { JSX } from 'react';
import { SearchFavoriteIcon } from '../favourite-icon';
import { SearchOption } from '../option';
import { type SearchOptionData, useSearchProvider } from '../provider';
import { SectionContainer } from '../section-container';

interface GroupsSectionProps {
  groups: Search[];
}

function getGroupPath(partyId: number, type: SearchType) {
  switch (type) {
    case SearchType.SubjectGroup:
      return `/groups/subject/${partyId}`;
    case SearchType.GeneralGroup:
      return `/groups/class/${partyId}`;
    // Need extra context through the meta to know if it's class or custom
    // case SearchType.GeneralGroup:
    //   return `/people/custom/${partyId}`;
    default:
      return '/groups/class';
  }
}

export function GroupOption({
  option,
  endIcon,
}: {
  option: SearchOptionData;
  endIcon?: JSX.Element;
}) {
  const { partyId, type, text, avatarUrl } =
    option as GroupsSectionProps['groups'][number];
  const { t } = useTranslation(['common']);

  return (
    <SearchOption path={getGroupPath(partyId, type)} optionData={option}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Avatar
              sx={{ width: 32, height: 32, fontSize: 14 }}
              name={text}
              src={avatarUrl}
            />
          </Box>
          <Stack>
            <Typography component="span" variant="body2">
              {text}
            </Typography>
            <Typography component="span" variant="caption">
              {t(`common:searchType.${type}`)}
            </Typography>
          </Stack>
        </Stack>
        {endIcon && <Box alignSelf="flex-end">{endIcon}</Box>}
      </Stack>
    </SearchOption>
  );
}

export function GroupsSection({ groups }: GroupsSectionProps) {
  const { t } = useTranslation(['common']);
  const { favouriteIds, toggleFavorite } = useSearchProvider();
  if (groups.length === 0) return null;

  return (
    <SectionContainer heading={t('common:groups')}>
      {groups?.map((option) => {
        const isFavorite = favouriteIds.has(option.partyId);
        return (
          <GroupOption
            key={`${option.partyId}-${option.text}`}
            option={option}
            endIcon={
              <SearchFavoriteIcon
                isFavorite={isFavorite}
                onClick={() => toggleFavorite(option.partyId, option)}
              />
            }
          />
        );
      })}
    </SectionContainer>
  );
}
